/**********************************************************************************************************
 *   BASE IMPORTS
 **********************************************************************************************************/
import styled from 'styled-components'

/**********************************************************************************************************
 *   SHARED IMPORTS
 **********************************************************************************************************/
import { Anchor, Theme } from '@nexigendigital/nxui'
import { useUpdateCartItemsMutation } from 'api/shop'
import { addToItems, removeFromItemsByNameID } from 'store/slices/shopSlice'
import { Availability, Cart, CartItem, PendingItem } from 'models/shop'
import { OrderType, ProductServiceType, Weight } from 'models/enums'
import { useAppDispatch } from 'store/hooks'

/**********************************************************************************************************
 *   INTERFACE
 **********************************************************************************************************/
interface Props {
    item: Availability,
    items: PendingItem[],
    addedItem: object | undefined,
    productType: string,
    domainName: string,
    cart: Cart | null | undefined
}

/**********************************************************************************************************
 *   COMPONENT START
 **********************************************************************************************************/
export default function SelectItem({ item, items, addedItem, productType, domainName, cart }: Props) {
    const dispatch = useAppDispatch()
    const [updateCartItems] = useUpdateCartItemsMutation()
    const { availability, premium, meta: { allow_id_protection: idProtection }, product: { id: product_id }, pricing, eligibility_fields } = item
    const { price, billing_cycle_id } = pricing.filter(({ type }) => type === 'register')[0]
    const hostingItems: any = items.filter((pendingItem: PendingItem) => pendingItem.product_type === ProductServiceType.SharedHosting)
    const hostingHasName = productType === 'web-hosting' ? hostingItems[hostingItems.length - 1].name !== undefined : false
    const itemInCart: any = items.find((pendingItem: PendingItem) => pendingItem.name === domainName && pendingItem.product_id === product_id)

    function addToCart() {
        if (cart && cart.uuid) {
            const newItem = {
                product_id,
                billing_cycle_id,
                name: domainName,
                order_type: OrderType.Register,
                ...(!premium && { auto_renew: true }),
                ...(idProtection && { id_protection: true })
            }
            updateCartItems({ uuid: cart.uuid, items: [newItem] })
        }
    }

    function removeFromCart() {
        if (cart && cart.uuid) {
            const cartItem = cart.items.find((cartItem: CartItem) => cartItem.product_id === product_id && cartItem.name === domainName)
            updateCartItems({ uuid: cart.uuid, remove_items: [cartItem?.item_uuid] })
        }
    }

    if (hostingHasName && itemInCart?.name !== domainName) {
        return null
    }

    if (!availability) {
        return <Taken>Taken</Taken>
    }

    if (addedItem) {
        return (
            <Anchor
                color={'primary'}
                onClick={() => {
                    dispatch(removeFromItemsByNameID({ product_id, name: domainName }))
                    removeFromCart()
                }}
            >
                Remove
            </Anchor>
        )
    }

    return (
        <Anchor
            color={'primary'}
            onClick={() => {
                const pendingItem = {
                    product_id,
                    name: domainName,
                    product_type: 'domain',
                    order_type: 'register',
                    billing_cycle_id,
                    eligibility_fields,
                    ...(!premium && { auto_renew: true }),
                    ...(idProtection && { id_protection: true })
                }
                dispatch(addToItems({
                    ...pendingItem,
                    pricing: price,
                    pricing_list: pricing
                }))
                addToCart()
            }}
        >
            Select
        </Anchor>
    )
}

/**********************************************************************************************************
 *   CUSTOM STYLING
 **********************************************************************************************************/
const Taken = styled.span`
    width: 60px;
    text-align: right;
    font-size: 12px;
    ${Theme.Functions.setFont('Inter', Weight.Medium)};
`
